import React, { useCallback, useContext, useState } from 'react';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import ExportModal from 'src/components/Modals/ExportModal';
import { useAppSelector, useQueryParamValue, useSnackbar } from 'src/utils/Hooks';
import { ExportCheckBox } from 'src/components/BeaconV2Components/common/AppExportModal/AppExportModal';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { ExportContext } from 'src/providers/ExportContextProvider';
import { ContentSubTabs, exportContentReq } from '../utils';
import { useLegacyExportLive } from './legacyExportLive';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';

const ProductExportModal = ({
  open,
  onClose,
  exportType
}: {
  open: boolean;
  onClose: () => void;
  exportType?: ContentSubTabs;
}) => {
  const queryParams = useAppSelector((state) => state.app.queryParams);
  const [isSeparatedByWeek, setIsSeparatedByWeek] = useState(false);
  const [isIncludeComparison, setIsIncludeComparison] = useState(false);
  const [isIncludeCurrentContent, setIsIncludeCurrentContent] = useState(false);
  const [selectedGroupBy, setSelectedGroupBy] = useState('Product');

  const getGroupByLabel = () => {
    if (exportType === ContentSubTabs.contentVariationsParent) {
      return 'Parent SKU';
    } else if (exportType === ContentSubTabs.contentVariationsChild) {
      return 'Child SKU';
    }
    return 'Product';
  };

  const groupByLabel = getGroupByLabel();
  const { startPolling } = useContext(ExportContext);
  const req = useLegacyExportLive();
  const { id: retailerId } = useAppSelector((state) => state.retailer);
  const { showSnackbar } = useSnackbar();
  const { appFilters } = useBeaconFilters();

  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  const mainEntityType = mainEntity?.type;
  const mainEntityId = mainEntity?.id;

  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: startComparisonWeek, endWeek: endComparisonWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const subTab = useQueryParamValue('subtab') || '';

  const resolvedExportType = exportType || ContentSubTabs.contentUtilization;

  const { categoryFilters, subcategoryFilters, segmentFilters } = appFilters;

  const handleExportClick = useCallback(async () => {
    try {
      if (isIncludeCurrentContent) {
        await req();
      } else {
        const exportParams = {
          retailerId,
          segmentIds: segmentFilters ? [segmentFilters.id] : [],
          categoryIds: (categoryFilters || []) as number[],
          subCategoryIds: (subcategoryFilters || []) as number[],
          stacklineSkus: [],
          brandIds: [],
          shouldGroupByWeek: isSeparatedByWeek,
          exportType: resolvedExportType,
          startWeekId: !isSeparatedByWeek ? endWeek : startWeek,
          endWeekId: endWeek,
          comparisonStartWeekId: isIncludeComparison
            ? !isSeparatedByWeek
              ? endComparisonWeek
              : startComparisonWeek
            : undefined,
          comparisonEndWeekId: isIncludeComparison ? endComparisonWeek : undefined
        };

        if (mainEntityType === 'product') {
          exportParams.stacklineSkus = [mainEntityId];
        } else if (mainEntityType === 'brand') {
          exportParams.brandIds = [Number(mainEntityId)];
        } else if (mainEntityType === 'category') {
          if (mainEntityId !== +0 && !exportParams.categoryIds.includes(Number(mainEntityId))) {
            exportParams.categoryIds = [...exportParams.categoryIds, Number(mainEntityId)];
          }
        } else if (mainEntityType === 'subcategory') {
          if (!exportParams.subCategoryIds.includes(Number(mainEntityId))) {
            exportParams.subCategoryIds = [...exportParams.subCategoryIds, Number(mainEntityId)];
          }
        } else if (mainEntityType === 'segment') {
          exportParams.segmentIds = [String(mainEntityId)];
        }

        await exportContentReq(exportParams);
      }

      showSnackbar({ type: 'success', message: 'Your download is processing.' });
      startPolling();
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    }
  }, [
    endComparisonWeek,
    endWeek,
    isIncludeComparison,
    isIncludeCurrentContent,
    isSeparatedByWeek,
    onClose,
    req,
    retailerId,
    showSnackbar,
    startComparisonWeek,
    startPolling,
    startWeek,
    categoryFilters,
    subcategoryFilters,
    segmentFilters,
    mainEntityType,
    mainEntityId,
    resolvedExportType
  ]);

  return (
    <ExportModal open={open} onClose={onClose}>
      <ExportModal.Header title="Export" onClose={onClose} />
      <ExportModal.Body
        isSeparatedByWeek={isSeparatedByWeek}
        isIncludeComparison={isIncludeComparison}
        setIsSeparatedByWeek={setIsSeparatedByWeek}
        setIsIncludeComparison={setIsIncludeComparison}
        customTimePeriodComponent={
          <>
            <ExportCheckBox
              isSelected={isSeparatedByWeek}
              id="separateDataByWeek"
              label="Separate data by week"
              onSelect={(e) => {
                setIsSeparatedByWeek(e.target.checked);
              }}
              checkboxProps={{
                disabled: isIncludeCurrentContent
              }}
            />
            {/* Include comparison checkbox */}
            <ExportCheckBox
              isSelected={isIncludeComparison}
              id="includeComparison"
              label="Include comparison time period"
              onSelect={(e) => setIsIncludeComparison(e.target.checked)}
              checkboxProps={{
                disabled: isIncludeCurrentContent
              }}
            />
            {!subTab.includes('contentVariations') && (
              <ExportCheckBox
                isSelected={isIncludeCurrentContent}
                id="includeCurrentContent"
                label="Include current content"
                onSelect={(e) => setIsIncludeCurrentContent(e.target.checked)}
                checkboxProps={{
                  disabled: isIncludeComparison || isSeparatedByWeek
                }}
              />
            )}
          </>
        }
      >
        <SlDropdownMenu
          defaultLabel={subTab.includes('contentVariations') ? groupByLabel : 'Product'}
          options={[{ id: 'Product', label: subTab.includes('contentVariations') ? groupByLabel : 'Product' }]}
          onChange={(option) => setSelectedGroupBy(option.id)}
          selectedId={selectedGroupBy}
        />
      </ExportModal.Body>
      <ExportModal.Footer
        searchParams={queryParams.searchParams}
        handleClose={onClose}
        handleExportClick={handleExportClick}
      />
    </ExportModal>
  );
};

export default ProductExportModal;
